import React, { useEffect, useState, useCallback, useRef } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import apiMcu from "api/mcu";
import { toastError } from "utils/helpers";
import { useTranslation } from "react-i18next";
import ClearIcon from '@mui/icons-material/Clear';

const PAGE_SIZE = 10;

const TYPE = {
    ROOM: 1,
    SIP: 2
}

export default function AutocompleteSearch({
    placeholder,
    nameDisplay,
    setValue,
    searchFor = TYPE.ROOM
}) {
    const [keyword, setKeyword] = useState(nameDisplay || "");
    const [users, setUsers] = useState([]);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const { t } = useTranslation();
    const refTimeout = useRef();
    const [enterSearch, setEnterSearch] = useState(false);

    // =============================
    // API CALL
    // =============================
    const apiGetListRooms = (roomNameOrRoomCode, currentPage, size) => {
        return apiMcu.getListRooms(roomNameOrRoomCode, currentPage, size);
    };

    const apiGetListSipAccount = (currentPage, userName) => {
        return apiMcu.getListSipAccount(currentPage, userName);
    }

    const fetchUsers = useCallback(async (search, pageNumber) => {
        if (loading) return;

        try {
            setLoading(true);

            if (searchFor === TYPE.ROOM) {
                apiGetListRooms(search, pageNumber, 10)
                    .then((result) => {

                        if (pageNumber === 0) {
                            setUsers(result?.data);
                        } else {
                            setUsers((prev) => [...prev, ...result?.data]);
                        }

                        // Nếu trả về ít hơn PAGE_SIZE => hết data
                        if (result?.data.length < PAGE_SIZE) {
                            setHasMore(false);
                        }

                        setPage(pageNumber + 1);
                    })
                    .catch((error) => {
                        toastError(t('mcu.toast.error'))
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            } else {
                apiGetListSipAccount(pageNumber, search)
                    .then((result) => {

                        if (pageNumber === 0) {
                            setUsers(result?.data);
                        } else {
                            setUsers((prev) => [...prev, ...result?.data]);
                        }

                        // Nếu trả về ít hơn PAGE_SIZE => hết data
                        if (result?.data.length < PAGE_SIZE) {
                            setHasMore(false);
                        }

                        setPage(pageNumber + 1);
                    })
                    .catch((error) => {
                        toastError(t('mcu.toast.error'))
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }

        } catch (error) {
            console.error("Fetch users error:", error);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    // =============================
    // Debounce search
    // =============================
    useEffect(() => {
        if (!keyword.trim()) {
            setUsers([]);
            setHasMore(false);
            return;
        }

        const delay = setTimeout(() => {
            setPage(0);
            setHasMore(true);
            fetchUsers(keyword, 0);
        }, 400);

        return () => clearTimeout(delay);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keyword]);

    // =============================
    // Infinite Scroll
    // =============================
    const handleScroll = (event) => {
        const listboxNode = event.currentTarget;

        const isBottom =
            listboxNode.scrollHeight - listboxNode.scrollTop <=
            listboxNode.clientHeight + 5;

        if (isBottom && hasMore && !loading) {
            if (refTimeout.current) {
                clearTimeout(refTimeout.current);
            }
            refTimeout.current = setTimeout(() => {
                fetchUsers(keyword, page);
            }, 300);
        }
    };

    return (
        <Autocomplete
            clearIcon={
                <ClearIcon fontSize="small" sx={{ display: 'none' }} />
            }
            inputValue={enterSearch ? keyword : nameDisplay}
            options={users}
            loading={loading}
            filterOptions={(x) => x} // Tắt filter client
            getOptionLabel={(option) => searchFor === TYPE.ROOM ? `${option.room_number} - ${option.room_name}` : option.username}
            onInputChange={(event, newInputValue) => {
                setKeyword(newInputValue);
            }}
            onChange={(_, values) => {
                setValue(values)
            }}
            renderOption={(props, option, { selected }) => (
                <li {...props}>
                    {searchFor === TYPE.ROOM ? `${option.room_number} - ${option.room_name}` : option.username}
                </li>
            )}
            ListboxProps={{
                onScroll: handleScroll,
                style: {
                    maxHeight: 250,
                    overflow: "auto",
                },
            }}
            noOptionsText={t('search.notfound')}
            renderInput={(params) => {
                return <TextField
                    {...params}
                    placeholder={placeholder}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    onChange={(e) => {
                        if(!enterSearch){
                            setEnterSearch(true);
                        }
                    }}
                />
            }}
        />
    );
}